import React, { useEffect, useMemo, useRef, useState } from 'react';
import { geoMercator, geoPath } from 'd3';
import {
  ArrowDownUp, ArrowRight, Check, Files, Goal,
  Landmark, ListFilter, MapPin, ScanSearch, Shield, ShieldAlert,
} from 'lucide-react';
import { accent, colors, propertyTypeGradients } from '../../config/designSystem';
import australia from './data/australia-outline.json';
import '../../styles/landing-property-story.css';

const PILLARS = [
  { name: 'Plan', title: 'What am I working towards?', caption: 'Turn the future you want into a property strategy shaped around you.' },
  { name: 'Finances', title: 'How are my finances looking?', caption: 'Know your capital, borrowing position, cash flow and room for surprises.' },
  { name: 'Discover', title: 'Where should I look next?', caption: 'See where members are researching, then compare which areas suit your brief.' },
  { name: 'Assess', title: 'Is this property right for me?', caption: 'Bring risks, value and your strategy together in one property decision.' },
  { name: 'Purchase', title: 'What should I offer and do next?', caption: 'Work through negotiation, paperwork and key dates, with decisions kept in your hands.' },
  { name: 'Manage', title: 'Is my portfolio still on track?', caption: 'The relationship continues: your position, your progress and what needs attention.' },
];
const DURATION = 30000;
const SEGMENT = DURATION / PILLARS.length;
const STORY_COLORS = {
  '--hw-bg': colors.bg.page,
  '--hw-paper': colors.bg.content,
  '--hw-ink': colors.text.primary,
  '--hw-muted': colors.text.secondary,
  '--hw-line': colors.border.default,
  '--hw-soft': colors.bg.sidebar,
  '--hw-accent': accent.rose[500],
  '--hw-accent-text': accent.rose[700],
  '--hw-primary': colors.interactive.hover,
  '--hw-indigo': accent.indigo[500],
  '--hw-map-line': colors.text.faint,
  '--hw-faint': propertyTypeGradients.villa.from,
};
const PLACES = [[115.86, -31.95], [144.96, -37.81], [151.21, -33.87], [153.03, -27.47]];

function ResearchMap({ editorial }) {
  const container = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = container.current;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const map = useMemo(() => {
    if (!size.width || !size.height) return null;
    // Published Natural Earth 1:110m geometry; activity markers are illustrative.
    // https://github.com/nvkelso/natural-earth-vector/blob/master/geojson/ne_110m_admin_0_countries.geojson
    const projection = geoMercator().fitExtent([[8, 8], [size.width - 8, size.height - 8]], australia);
    return { path: geoPath(projection)(australia), points: PLACES.map((point) => projection(point)) };
  }, [size]);

  return (
    <div className="hw-map-wrap hw-beat" ref={container}>
      <svg className="hw-map" viewBox={`0 0 ${size.width || 1} ${size.height || 1}`} role="img" aria-label="Australia with illustrative research-activity markers, not live data">
        <title>Illustrative member research activity across Australia</title>
        {map && <>
          <path className="hw-land" d={map.path} />
          {map.points.map(([x, y], index) => (
            <g className="hw-map-pin" key={index}>
              {editorial ? <path d={`M${x - 5},${y}h10M${x},${y - 5}v10`} stroke="var(--hw-primary)" strokeWidth="1.5" /> : <><circle cx={x} cy={y} r="12" fill="var(--hw-faint)" stroke="var(--hw-accent)" /><circle cx={x} cy={y} r="4" fill="var(--hw-accent)" stroke="var(--hw-paper)" /></>}
            </g>
          ))}
        </>}
      </svg>
    </div>
  );
}

function Scene({ index, active, children }) {
  return (
    <div className={`hw-scene${active === index ? ' is-current' : ''}`} data-scene={index} aria-hidden={active !== index} inert={active !== index ? '' : undefined}>
      {children}
    </div>
  );
}

function PortfolioBuilding({ type }) {
  return (
    <svg viewBox="0 0 72 64" fill="none" stroke="currentColor" strokeWidth="1" aria-hidden="true">
      <path d="M3 59h66" />
      {type === 'house' ? <>
        <path d="M8 30 35 9l28 21M13 27v32h45V27M39 13V6h8v13M27 59V42h12v17M19 31h9v8h-9zM43 31h9v8h-9zM16 23l19-15 25 19" />
      </> : type === 'townhouse' ? <>
        <path d="M8 25 24 13l16 12 15-12 14 12M12 23v36h52V23M38 24v35M18 30h9v8h-9zM46 30h9v8h-9zM19 59V46h8v13M47 59V46h8v13" />
      </> : <>
        <path d="M17 59V10h38v49M13 10h46M24 59V48h9v11M39 18h9v8h-9zM24 18h9v8h-9zM39 33h9v8h-9zM24 33h9v8h-9zM39 48h9v8h-9z" />
      </>}
    </svg>
  );
}

export default function LandingPropertyStory({ editorial = false }) {
  const root = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let elapsed = 0;
    let previous = 0;
    let current = 0;
    let running = false;
    let inView = false;
    let frame = 0;

    function paint() {
      const next = Math.max(0, Math.min(PILLARS.length - 1, Math.floor(elapsed / SEGMENT)));
      if (next !== current) {
        current = next;
        setActive(next);
      }
    }
    function pause() {
      running = false;
      cancelAnimationFrame(frame);
    }
    function tick(now) {
      if (!running) return;
      elapsed = (elapsed + (previous ? Math.max(0, now - previous) : 0)) % DURATION;
      previous = now;
      paint();
      frame = requestAnimationFrame(tick);
    }
    function play() {
      if (running) return;
      previous = 0;
      running = true;
      paint();
      frame = requestAnimationFrame(tick);
    }
    function syncPlayback() {
      if (inView && !document.hidden && !reduced.matches) play();
      else pause();
    }
    const surface = root.current;
    reduced.addEventListener('change', syncPlayback);
    document.addEventListener('visibilitychange', syncPlayback);
    const observer = new IntersectionObserver((entries) => {
      inView = entries.some((entry) => entry.isIntersecting);
      syncPlayback();
    }, { threshold: 0 });
    observer.observe(surface);

    return () => {
      running = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      reduced.removeEventListener('change', syncPlayback);
      document.removeEventListener('visibilitychange', syncPlayback);
    };
  }, []);

  const pillar = PILLARS[active];

  return (
    <div className="hw-story" id="property-story" ref={root} style={STORY_COLORS} role="region" aria-label="How Heyward works for you">
      <div className="hw-film" data-step={active} aria-hidden="true">
        <div className="hw-heading"><div className="hw-chapter">{String(active + 1).padStart(2, '0')} / {pillar.name}</div><h2>{pillar.title}</h2></div>
        <div className="hw-stage">
          <figure className="hw-house"><img src="/sketched-house.png" width="512" height="512" alt="An illustrative property, carried through the journey" decoding="async" /></figure>
          <Scene index={0} active={active}>
            <div className="hw-goal hw-paper hw-beat">
              <span className="hw-label">Your goal</span><h3>More freedom.<br />A plan to get there.</h3>
              <div className="hw-traits"><span>Long-term income</span><span>Cautious risk</span></div>
            </div>
            <div className="hw-route hw-beat hw-delay-1"><span>Your position</span><span>Your strategy</span><span>Your goal</span></div>
          </Scene>
          <Scene index={1} active={active}>
            <div className="hw-finance hw-paper hw-beat">
              <span className="hw-label">Your financial picture</span>
              <div className="hw-inputs">
                <div className="hw-input hw-beat"><Landmark aria-hidden="true" /><strong>Capital</strong><small>Cash + equity</small></div>
                <div className="hw-input hw-beat hw-delay-1"><ArrowDownUp aria-hidden="true" /><strong>Cash flow</strong><small>Income − costs</small></div>
                <div className="hw-input hw-beat hw-delay-2"><Shield aria-hidden="true" /><strong>Resilience</strong><small>Buffer + stress tests</small></div>
              </div>
              <h3>Understand your position.<br />See what you can do next.</h3>
              <div className="hw-finance-checks"><span><Check aria-hidden="true" />Borrowing position</span><span><Check aria-hidden="true" />Room for surprises</span></div>
            </div>
          </Scene>
          <Scene index={2} active={active}>
            <ResearchMap editorial={editorial} />
            <div className="hw-locate hw-paper hw-beat hw-delay-1">
              <span className="hw-label">Find your next area</span><h3>Follow the signals.<br />Apply your brief.</h3>
              <div className="hw-signal"><ScanSearch aria-hidden="true" />Member research activity</div>
              <div className="hw-signal"><MapPin aria-hidden="true" />Suburb comparisons</div>
              <div className="hw-signal"><ListFilter aria-hidden="true" />Your budget + preferences</div>
            </div>
            <div className="hw-map-credit">Activity: illustration · Map: Natural Earth</div>
          </Scene>
          <Scene index={3} active={active}>
            <div className="hw-assess hw-paper hw-beat">
              <span className="hw-label">This property, for you</span><h3>Look beyond<br />the listing.</h3>
              <div className="hw-evidence-row hw-beat hw-delay-1"><ShieldAlert aria-hidden="true" />Risks + missing evidence</div>
              <div className="hw-evidence-row hw-beat hw-delay-2"><Files aria-hidden="true" />Value + rental evidence</div>
              <div className="hw-evidence-row hw-beat hw-delay-2"><Goal aria-hidden="true" />Fit with your strategy</div>
              <p className="hw-fit-note">A good property still has to be right for your plan.</p>
            </div>
          </Scene>
          <Scene index={4} active={active}>
            <div className="hw-offer hw-paper hw-beat">
              <div className="hw-offer-header"><span className="hw-label">Your next move</span><span className="hw-stamp">FOR YOUR REVIEW</span></div>
              <h3>A counter-offer,<br />with the reasoning behind it.</h3>
              <div className="hw-offer-logic"><span>Fair value</span><span>Offer strategy</span><span>Walk-away price</span></div>
              <div className="hw-offer-footer"><span>Reply drafted</span><b>You decide what goes.</b></div>
            </div>
            <div className="hw-purchase-path hw-beat hw-delay-2"><span>Offer</span><ArrowRight aria-hidden="true" /><span>Contract</span><ArrowRight aria-hidden="true" /><span>Key dates</span><ArrowRight aria-hidden="true" /><span>Settlement</span></div>
          </Scene>
          <Scene index={5} active={active}>
            <div className="hw-portfolio hw-beat">
              <div className="hw-portfolio-head"><span className="hw-label">Your portfolio</span><span>Illustrative holdings</span></div>
              <div className="hw-portfolio-columns"><span>Property</span><span>What needs a look</span></div>
              <div className="hw-property-row hw-beat">
                <PortfolioBuilding type="house" />
                <div><strong>Newcastle, NSW</strong><span>House</span></div>
                <span className="hw-property-review">Rent review</span>
              </div>
              <div className="hw-property-row hw-beat hw-delay-1">
                <PortfolioBuilding type="townhouse" />
                <div><strong>Geelong, VIC</strong><span>Townhouse</span></div>
                <span className="hw-property-review">Loan review</span>
              </div>
              <div className="hw-property-row hw-beat hw-delay-2">
                <PortfolioBuilding type="apartment" />
                <div><strong>Brisbane, QLD</strong><span>Apartment</span></div>
                <span className="hw-property-review">Costs changed</span>
              </div>
              <div className="hw-portfolio-summary"><span>Equity</span><span>Cash flow</span><span>Goal progress</span></div>
            </div>
          </Scene>
        </div>
        <div className="hw-personal">{!editorial && <span className="hw-thread-dot" aria-hidden="true" />}<strong>Your brief travels with you</strong><span>Long-term income · Cautious risk</span></div>
        <div className="hw-caption">{pillar.caption}</div>
        <div className="hw-example-note">Illustrative example</div>
      </div>
      <div className="hw-accessible">
        <span className="hw-label">Your property journey</span>
        <h2>With you at every step.</h2>
        <ol>{PILLARS.map((item) => <li key={item.name}><h3>{item.title}</h3><p>{item.caption}</p></li>)}</ol>
      </div>
    </div>
  );
}
